import React from 'react';
import {
  Box, Typography, Paper, TextField, Button, Checkbox, FormControlLabel,
  Table, TableHead, TableRow, TableCell, TableBody, Chip, Divider, Alert,
  LinearProgress
} from '@mui/material';
import Colors from '../../../components/ui/Colors';
import ColumnReviewPanel from './ColumnReviewPanel';
import {
  submitColumnKnowledge as apiSubmitColumnKnowledge,
  acceptColumn as apiAcceptColumn,
  overrideColumn as apiOverrideColumn,
  cancelReview as apiCancelReview,
} from '../../../utilityFunctions/AdminUtil';
import swal from 'sweetalert';

const SectionLabel = ({ label }) => (
  <Typography variant="overline" sx={{ color: Colors.primary, fontWeight: 600, display: 'block', mt: 1.5, mb: 0.5 }}>
    {label}
  </Typography>
);

export default function PreviewPanel(props) {
  const { preview, onCancel, onConfirm, committing, commitJob, jwtToken } = props;

  const [currentMetadata, setCurrentMetadata] = React.useState(
    (preview && preview.metadata) || null
  );
  const [reviewState, setReviewState] = React.useState(
    (preview && preview.reviewState) || null
  );
  const [busyColumn, setBusyColumn] = React.useState(null);

  const [editedDescription, setEditedDescription] = React.useState(
    (preview && preview.metadata && preview.metadata.descriptor && preview.metadata.descriptor.description) || ''
  );
  const [editedBusinessPurpose, setEditedBusinessPurpose] = React.useState(
    (preview && preview.metadata && preview.metadata.businessPurpose) || ''
  );
  const [editedExampleQuestions, setEditedExampleQuestions] = React.useState(() => {
    const eq = preview && preview.metadata && preview.metadata.descriptor && preview.metadata.descriptor.exampleQuestions;
    return Array.isArray(eq) ? eq.join('\n') : '';
  });
  const [columns, setColumns] = React.useState(() => {
    const inferred = (preview && preview.schema && preview.schema.columns) || [];
    return inferred.map((c) => ({
      name: c.name,
      sqlType: c.sqlType,
      nullable: c.nullable,
      sampleValues: c.sampleValues || [],
      suspectedPii: !!c.suspectedPii,
    }));
  });

  if (!preview) return null;

  const schema = preview.schema || {};
  const sampleRows = schema.sampleRows || [];
  const headers = (schema.columns || []).map((c) => c.name);

  const reviewActive = !!reviewState;
  const readyToCommit = !reviewActive || !!reviewState.ready_to_commit;

  const togglePii = (idx) => {
    setColumns((prev) => prev.map((c, i) => (i === idx ? { ...c, suspectedPii: !c.suspectedPii } : c)));
  };

  const applyReviewResponse = (data) => {
    if (!data) return;
    if (data.reviewState) setReviewState(data.reviewState);
    if (data.metadata) setCurrentMetadata(data.metadata);
  };

  const reportReviewError = (action, error) => {
    swal({
      title: `${action} failed`,
      text: error || 'Unknown error',
      icon: 'error',
    });
  };

  const handleSubmitKnowledge = async (columnName, knowledge) => {
    if (!preview.stagingId) return false;
    setBusyColumn(columnName);
    const result = await apiSubmitColumnKnowledge(preview.stagingId, columnName, knowledge, jwtToken);
    setBusyColumn(null);
    if (!result.ok) { reportReviewError('Submit knowledge', result.error); return false; }
    applyReviewResponse(result.data);
    return true;
  };

  const handleAcceptColumn = async (columnName) => {
    if (!preview.stagingId) return false;
    setBusyColumn(columnName);
    const result = await apiAcceptColumn(preview.stagingId, columnName, jwtToken);
    setBusyColumn(null);
    if (!result.ok) { reportReviewError('Accept', result.error); return false; }
    applyReviewResponse(result.data);
    return true;
  };

  const handleOverrideColumn = async (columnName, body) => {
    if (!preview.stagingId) return false;
    setBusyColumn(columnName);
    const result = await apiOverrideColumn(preview.stagingId, columnName, body, jwtToken);
    setBusyColumn(null);
    if (!result.ok) { reportReviewError('Override', result.error); return false; }
    applyReviewResponse(result.data);
    return true;
  };

  const buildEditedMetadata = () => {
    const base = currentMetadata || preview.metadata || {};
    const md = JSON.parse(JSON.stringify(base));
    if (!md.descriptor) md.descriptor = {};
    if (!md.definition) md.definition = {};

    md.descriptor.description = editedDescription;
    md.businessPurpose = editedBusinessPurpose;
    md.descriptor.exampleQuestions = editedExampleQuestions
      .split('\n')
      .map((s) => s.trim())
      .filter((s) => s.length > 0);

    if (Array.isArray(md.definition.columns)) {
      const piiByName = new Map(columns.map((c) => [c.name, c.suspectedPii]));
      md.definition.columns = md.definition.columns.map((col) => {
        if (piiByName.has(col.name)) {
          return { ...col, pii: piiByName.get(col.name) };
        }
        return col;
      });
    }

    return md;
  };

  const handleConfirm = () => {
    onConfirm(buildEditedMetadata());
  };

  const handleCancel = () => {
    // Fire-and-forget: the server-side staging entry will TTL-expire anyway,
    // but dropping it now frees the slot and the admin's audit trail is clean.
    if (preview && preview.stagingId && jwtToken) {
      apiCancelReview(preview.stagingId, jwtToken);
    }
    onCancel();
  };

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Review the inferred schema and metadata. Edits made here will be persisted on commit. Nothing is created in Databricks until you confirm.
      </Alert>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <SectionLabel label="Table" />
        <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>{schema.tableName}</Typography>
        <Typography variant="caption" color="text.secondary">
          Source: {schema.sourceFilename} ({schema.detectedFormat})
          {' · '}
          Total rows in source: {schema.totalRowCount}
        </Typography>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <SectionLabel label="Description (editable)" />
        <TextField
          fullWidth
          multiline
          minRows={2}
          value={editedDescription}
          onChange={(e) => setEditedDescription(e.target.value)}
        />

        <SectionLabel label="Business purpose (editable)" />
        <TextField
          fullWidth
          multiline
          minRows={2}
          value={editedBusinessPurpose}
          onChange={(e) => setEditedBusinessPurpose(e.target.value)}
        />

        <SectionLabel label="Example questions (one per line, editable)" />
        <TextField
          fullWidth
          multiline
          minRows={3}
          value={editedExampleQuestions}
          onChange={(e) => setEditedExampleQuestions(e.target.value)}
        />
      </Paper>

      {reviewActive && (
        <ColumnReviewPanel
          reviewState={reviewState}
          metadata={currentMetadata}
          busyColumn={busyColumn}
          onSubmitKnowledge={handleSubmitKnowledge}
          onAccept={handleAcceptColumn}
          onOverride={handleOverrideColumn}
        />
      )}

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <SectionLabel label="Columns" />
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>SQL Type</TableCell>
                <TableCell>Nullable</TableCell>
                <TableCell>PII</TableCell>
                <TableCell>Sample values</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {columns.map((col, idx) => (
                <TableRow key={col.name}>
                  <TableCell sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{col.name}</TableCell>
                  <TableCell><Chip size="small" label={col.sqlType} /></TableCell>
                  <TableCell>{col.nullable ? 'yes' : 'no'}</TableCell>
                  <TableCell>
                    <FormControlLabel
                      control={<Checkbox size="small" checked={col.suspectedPii} onChange={() => togglePii(idx)} />}
                      label=""
                    />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 320, fontSize: '0.78rem', color: 'text.secondary', wordBreak: 'break-word' }}>
                    {(col.sampleValues || []).slice(0, 5).join(', ')}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <SectionLabel label={`First ${sampleRows.length} rows`} />
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {headers.map((h) => (
                  <TableCell key={h} sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sampleRows.map((row, rowIdx) => (
                <TableRow key={rowIdx}>
                  {headers.map((h) => (
                    <TableCell key={h} sx={{ fontSize: '0.75rem' }}>
                      {row && row[h] != null ? String(row[h]) : ''}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Paper>

      {committing && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          {(() => {
            const total = (commitJob && commitJob.rowsTotal) || 0;
            const inserted = (commitJob && commitJob.rowsInserted) || 0;
            const message = (commitJob && commitJob.message) || 'Working…';
            const determinate = total > 0 && inserted > 0;
            const pct = determinate ? Math.min(100, Math.round((inserted / total) * 100)) : 0;
            return (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: Colors.primary }}>
                    {message}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {total > 0 ? `${inserted.toLocaleString()} / ${total.toLocaleString()} rows${determinate ? ` (${pct}%)` : ''}` : ''}
                  </Typography>
                </Box>
                <LinearProgress
                  variant={determinate ? 'determinate' : 'indeterminate'}
                  value={pct}
                />
                <Alert severity="info" sx={{ mt: 1.5 }}>
                  Injection is in progress. This runs on the server, so you can wait here until it
                  finishes — or safely leave this page and continue working. Either way, we'll email
                  you the moment it completes (or if it fails).
                </Alert>
              </Box>
            );
          })()}
        </Paper>
      )}

      <Divider sx={{ my: 2 }} />

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button variant="outlined" onClick={handleCancel} disabled={committing}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleConfirm} disabled={committing || !readyToCommit}
          sx={{ backgroundColor: Colors.primary }}>
          {committing ? 'Injecting…' : (readyToCommit ? 'Confirm & inject' : 'Finish review to inject')}
        </Button>
      </Box>
    </Box>
  );
}
